import {
  deleteV1CompaniesCompanyIdSettlementExportLineDescriptionFormatsSide,
  getV1CompaniesCompanyIdSettlementExportLineDescriptionFormats,
  putV1CompaniesCompanyIdSettlementExportLineDescriptionFormatsSide,
  SettlementExportLineDescriptionField,
  SettlementExportSide,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { SettlementExportLineDescriptionFormat } from '~hooks/useSettlementExportLineDescriptionFormats/models';
import connection from '~services/connectionModule';

interface SaveLineDescriptionFormatParams {
  companyId: string;
  side: SettlementExportSide;
  fields: SettlementExportLineDescriptionField[];
}

interface ResetLineDescriptionFormatParams {
  companyId: string;
  side: SettlementExportSide;
}

export default function useSettlementExportLineDescriptionFormats() {
  // a company has at most one format per side, so the first page holds all of them
  const getLineDescriptionFormats = useCallback(async (companyId: string) => {
    try {
      const response =
        await getV1CompaniesCompanyIdSettlementExportLineDescriptionFormats({
          path: { 'company-id': companyId },
        });

      return response.data.data.map((format) =>
        SettlementExportLineDescriptionFormat.parse(format),
      );
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.settlement_export_line_description_formats.failed_to_fetch'),
      );
    }
  }, []);

  const saveLineDescriptionFormat = useCallback(
    async ({ companyId, side, fields }: SaveLineDescriptionFormatParams) => {
      try {
        const response =
          await putV1CompaniesCompanyIdSettlementExportLineDescriptionFormatsSide({
            path: { 'company-id': companyId, side },
            body: { fields },
          });

        return SettlementExportLineDescriptionFormat.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlement_export_line_description_formats.failed_to_save'),
        );
      }
    },
    [],
  );

  const resetLineDescriptionFormat = useCallback(
    async ({ companyId, side }: ResetLineDescriptionFormatParams) => {
      try {
        await deleteV1CompaniesCompanyIdSettlementExportLineDescriptionFormatsSide({
          path: { 'company-id': companyId, side },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlement_export_line_description_formats.failed_to_reset'),
        );
      }
    },
    [],
  );

  return {
    getLineDescriptionFormats,
    resetLineDescriptionFormat,
    saveLineDescriptionFormat,
  };
}
